import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import Header from './Header';
import Footer from './Footer';

const ResetPasswordPage: React.FC = () => {
  const { session, isLoaded } = useSession();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Arriving from the reset email, Supabase puts a recovery token in the URL
  // hash and exchanges it for a session on load. Capture its presence once at
  // mount — Supabase clears the hash after the exchange, so reading it on a
  // later render would give a false negative (and bounce a valid recovery
  // user off this page).
  const [hasRecoveryToken] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.location.hash.includes('access_token')
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setDone(true);
  };

  // A normally signed-in user (no recovery token) has no reason to be here —
  // password changes live in Account. Recovery sessions always arrive with a
  // token, so they're unaffected by this redirect.
  if (isLoaded && session && !hasRecoveryToken) {
    return <Navigate to="/account" replace />;
  }

  let body: React.ReactNode;
  if (done) {
    body = (
      <div className="card w-full max-w-md p-8 text-center space-y-4">
        <h1 className="font-display text-3xl text-charcoal">Password updated</h1>
        <p className="text-sm text-charcoal-light">You&rsquo;re all set and signed in.</p>
        <Link to="/my-list" className="btn-primary justify-center">
          Go to my list
        </Link>
      </div>
    );
  } else if (!isLoaded || (hasRecoveryToken && !session)) {
    body = (
      <div
        className="card w-full max-w-md p-8 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-label="Verifying reset link"
      >
        <div className="w-2 h-2 rounded-full bg-charcoal/20 animate-pulse" />
      </div>
    );
  } else if (!session) {
    body = (
      <div className="card w-full max-w-md p-8 text-center space-y-3">
        <h1 className="font-display text-3xl text-charcoal">Link expired</h1>
        <p className="text-sm text-charcoal-light">
          This password reset link is invalid or has expired.
        </p>
        <Link to="/forgot-password" className="text-amber hover:underline text-sm">
          Send a new reset link
        </Link>
      </div>
    );
  } else {
    body = (
      <form
        onSubmit={handleSubmit}
        className="card w-full max-w-md p-8 space-y-5"
        aria-labelledby="reset-heading"
      >
        <h1 id="reset-heading" className="font-display text-3xl text-charcoal mb-1">
          Choose a new password
        </h1>
        <p className="text-sm text-charcoal-light">
          Enter a new password for your account.
        </p>

        <label className="block text-sm">
          <span className="text-charcoal-light">New password</span>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
          />
          <span className="text-xs text-charcoal-light/80">At least 8 characters.</span>
        </label>

        {error && (
          <p className="text-sm text-terracotta" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || password.length < 8}
          className="btn-primary w-full justify-center disabled:opacity-50"
        >
          {submitting ? 'Updating…' : 'Update password'}
        </button>
      </form>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">{body}</main>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
